import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Megaphone, Pencil, ChevronRight, Activity, Search, X } from 'lucide-react';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { PageHeader } from '../components/ui/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { Modal, ModalFooter } from '../components/ui/Modal.tsx';
import { Input, Select } from '../components/ui/Input.tsx';
import { CampaignStatusBadge, ChannelBadge } from '../components/ui/Badge.tsx';
import { EmptyState } from '../components/ui/EmptyState.tsx';
import { Pagination } from '../components/ui/Pagination.tsx';
import { SkeletonRows } from '../components/ui/Skeleton.tsx';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.tsx';
import { useToast } from '../components/ui/Toast.tsx';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Campaign {
  id: string;
  name: string;
  channel: string;
  status: string;
  totalRecipients: number;
  sentCount: number;
  failedCount: number;
  skippedCount: number;
  bounceCount: number;
  complaintCount: number;
  pauseReason: string | null;
  scheduledAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

interface Template {
  id: string;
  name: string;
  channel: string;
  status: string;
}
interface Segment {
  id: string;
  name: string;
  type: string;
}

type CampaignForm = {
  name: string;
  channel: 'email' | 'sms';
  templateId: string;
  segmentId: string;
  scheduledAt: string;
};

type StatusFilter = 'all' | 'draft' | 'running' | 'paused' | 'completed' | 'cancelled';
const STATUS_TABS: StatusFilter[] = ['all', 'draft', 'running', 'paused', 'completed', 'cancelled'];

function formatDate(d: string) {
  return new Date(d).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function ProgressBar({ sent, total }: { sent: number; total: number }) {
  const pct = total > 0 ? Math.round((sent / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-zinc-100 rounded-full overflow-hidden min-w-[60px]">
        <div
          className="h-full bg-zinc-700 rounded-full transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs tabular-nums text-zinc-500 whitespace-nowrap">{pct}%</span>
    </div>
  );
}

function DeliveryHealthIcon({ bounceCount, sentCount }: { bounceCount: number; sentCount: number }) {
  if (sentCount === 0) return <span className="text-zinc-300 text-xs">—</span>;
  const rate = bounceCount / sentCount;
  if (rate > 0.05) {
    return (
      <span className="flex items-center gap-1 text-xs text-red-600 font-medium">
        <Activity size={11} className="text-red-500" />
        {(rate * 100).toFixed(1)}%
      </span>
    );
  }
  if (rate > 0.02) {
    return (
      <span className="flex items-center gap-1 text-xs text-amber-600 font-medium">
        <Activity size={11} className="text-amber-500" />
        {(rate * 100).toFixed(1)}%
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
      <Activity size={11} className="text-emerald-500" />
      {(rate * 100).toFixed(1)}%
    </span>
  );
}

const PAGE_SIZE = 25;

const EMPTY_FORM: CampaignForm = {
  name: '',
  channel: 'email',
  templateId: '',
  segmentId: '',
  scheduledAt: '',
};

// ─── Page ─────────────────────────────────────────────────────────────────────

export function CampaignsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();

  const [showCreate, setShowCreate] = useState(false);
  const [editCampaign, setEditCampaign] = useState<Campaign | null>(null);
  const [cancelTarget, setCancelTarget] = useState<Campaign | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [channelFilter, setChannelFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [form, setForm] = useState<CampaignForm>(EMPTY_FORM);

  const showModal = showCreate || !!editCampaign;

  const { data: campaigns = [], isLoading } = useQuery<Campaign[]>({
    queryKey: ['campaigns'],
    queryFn: () => api.get('/api/v1/campaigns').then((r) => r.data),
    refetchInterval: 10_000,
  });

  const { data: templates = [] } = useQuery<Template[]>({
    queryKey: ['templates'],
    queryFn: () => api.get('/api/v1/templates').then((r) => r.data),
    enabled: showModal,
  });

  const { data: segments = [] } = useQuery<Segment[]>({
    queryKey: ['segments'],
    queryFn: () => api.get('/api/v1/segments').then((r) => r.data),
    enabled: showModal,
  });

  // ── filtered list ────────────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    let result =
      statusFilter === 'all' ? campaigns : campaigns.filter((c) => c.status === statusFilter);
    if (channelFilter) result = result.filter((c) => c.channel === channelFilter);
    if (search) result = result.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()));
    return result;
  }, [campaigns, statusFilter, channelFilter, search]);

  const paginatedCampaigns = useMemo(
    () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filtered, page],
  );
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);

  const counts = useMemo(
    () => ({
      all: campaigns.length,
      draft: campaigns.filter((c) => c.status === 'draft').length,
      running: campaigns.filter((c) => c.status === 'running').length,
      paused: campaigns.filter((c) => c.status === 'paused').length,
      completed: campaigns.filter((c) => c.status === 'completed').length,
      cancelled: campaigns.filter((c) => c.status === 'cancelled').length,
    }),
    [campaigns],
  );

  // ── mutations ────────────────────────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: (data: CampaignForm) =>
      api
        .post('/api/v1/campaigns', {
          name: data.name,
          channel: data.channel,
          templateId: data.templateId,
          segmentId: data.segmentId || undefined,
          scheduledAt: data.scheduledAt || undefined,
        })
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      setShowCreate(false);
      setForm(EMPTY_FORM);
      toast.success('Campaign created');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to create campaign')),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CampaignForm> }) =>
      api
        .patch(`/api/v1/campaigns/${id}`, {
          name: data.name,
          templateId: data.templateId || undefined,
          segmentId: data.segmentId || undefined,
          scheduledAt: data.scheduledAt || undefined,
        })
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      setEditCampaign(null);
      toast.success('Campaign updated');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to update campaign')),
  });

  const launchMutation = useMutation({
    mutationFn: (id: string) => api.post(`/api/v1/campaigns/${id}/launch`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      toast.success('Campaign launched');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to launch')),
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => api.post(`/api/v1/campaigns/${id}/cancel`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      setCancelTarget(null);
      toast.info('Campaign cancelled');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to cancel')),
  });

  // ── modal helpers ────────────────────────────────────────────────────────────

  function openCreate() {
    setForm(EMPTY_FORM);
    setShowCreate(true);
  }

  function openEdit(c: Campaign) {
    setForm({
      name: c.name,
      channel: c.channel as 'email' | 'sms',
      templateId: '',
      segmentId: '',
      scheduledAt: c.scheduledAt ? c.scheduledAt.slice(0, 16) : '',
    });
    setEditCampaign(c);
  }

  function closeModal() {
    setShowCreate(false);
    setEditCampaign(null);
  }

  function handleSubmit() {
    if (editCampaign) {
      updateMutation.mutate({ id: editCampaign.id, data: form });
    } else {
      createMutation.mutate(form);
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const availableTemplates = templates.filter(
    (t) => t.channel === form.channel && t.status === 'active',
  );

  return (
    <div>
      <PageHeader
        title="Campaigns"
        description="Bulk email and SMS campaigns to audience segments"
        action={
          <Button onClick={openCreate} size="sm">
            + New campaign
          </Button>
        }
      />

      {/* Status tabs + channel filter */}
      <div className="flex items-center gap-3 mb-4 border-b border-zinc-100 pb-0">
        <div className="flex items-center gap-1 flex-1">
          {STATUS_TABS.map((s) => (
            <button
              key={s}
              onClick={() => { setStatusFilter(s); setPage(1); }}
              className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors capitalize ${
                statusFilter === s
                  ? 'border-zinc-900 text-zinc-900'
                  : 'border-transparent text-zinc-400 hover:text-zinc-700'
              }`}
            >
              {s}
              <span
                className={`ml-1.5 text-xs tabular-nums ${statusFilter === s ? 'text-zinc-500' : 'text-zinc-300'}`}
              >
                {counts[s]}
              </span>
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative pb-2">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search campaigns…"
            className="h-9 pl-9 pr-8 bg-white border border-zinc-200 rounded-lg text-sm text-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 w-56"
          />
          {search && (
            <button onClick={() => { setSearch(''); setPage(1); }} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-300 hover:text-zinc-500">
              <X size={13} />
            </button>
          )}
        </div>

        {/* Channel filter */}
        <div className="relative pb-2">
          <select
            value={channelFilter}
            onChange={(e) => { setChannelFilter(e.target.value); setPage(1); }}
            className="h-8 pl-3 pr-7 bg-white border border-zinc-200 rounded-lg text-xs text-zinc-700 focus:outline-none appearance-none"
          >
            <option value="">All channels</option>
            <option value="email">Email</option>
            <option value="sms">SMS</option>
          </select>
          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 text-[10px] pb-2">
            ▾
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-100">
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Channel
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider min-w-[120px]">
                Progress
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Bounce
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Created
              </th>
              <th className="px-4 py-3 w-32" />
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-50">
            {isLoading ? (
              <SkeletonRows count={5} cols={7} />
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <EmptyState
                    icon={<Megaphone size={28} />}
                    title={
                      statusFilter !== 'all' || channelFilter || search
                        ? 'No campaigns match'
                        : 'No campaigns yet'
                    }
                    description="Create a campaign to send bulk messages to an audience segment."
                    action={{ label: '+ New campaign', onClick: openCreate }}
                  />
                </td>
              </tr>
            ) : (
              paginatedCampaigns.map((c) => (
                <tr
                  key={c.id}
                  className="hover:bg-zinc-50/60 transition-colors group cursor-pointer"
                  onClick={() => navigate(`/campaigns/${c.id}`)}
                >
                  <td className="px-4 py-3 font-medium text-zinc-900 max-w-[200px] truncate">
                    {c.name}
                  </td>
                  <td className="px-4 py-3">
                    <ChannelBadge channel={c.channel} />
                  </td>
                  <td className="px-4 py-3">
                    <CampaignStatusBadge status={c.status} pauseReason={c.pauseReason} />
                  </td>
                  <td className="px-4 py-3">
                    {c.status === 'running' || c.status === 'completed' || c.status === 'paused' ? (
                      <div>
                        <ProgressBar sent={c.sentCount} total={c.totalRecipients} />
                        <p className="text-[10px] text-zinc-400 mt-0.5 tabular-nums">
                          {c.sentCount.toLocaleString()} / {c.totalRecipients.toLocaleString()}
                          {c.failedCount > 0 && (
                            <span className="text-red-400 ml-1">· {c.failedCount} failed</span>
                          )}
                          {c.skippedCount > 0 && (
                            <span className="text-zinc-400 ml-1">· {c.skippedCount} skipped</span>
                          )}
                        </p>
                      </div>
                    ) : (
                      <span className="text-xs text-zinc-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <DeliveryHealthIcon bounceCount={c.bounceCount ?? 0} sentCount={c.sentCount} />
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-500">{formatDate(c.createdAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {c.status === 'draft' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openEdit(c);
                          }}
                          className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-md"
                          title="Edit"
                        >
                          <Pencil size={12} />
                        </button>
                      )}
                      {(c.status === 'draft' || c.status === 'scheduled') && (
                        <Button
                          size="sm"
                          variant="secondary"
                          loading={launchMutation.isPending}
                          onClick={(e) => {
                            e.stopPropagation();
                            launchMutation.mutate(c.id);
                          }}
                        >
                          Launch
                        </Button>
                      )}
                      {!['completed', 'cancelled'].includes(c.status) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCancelTarget(c);
                          }}
                        >
                          Cancel
                        </Button>
                      )}
                      <ChevronRight size={13} className="text-zinc-300" />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
      )}

      {/* Create / Edit Modal */}
      <Modal
        open={showModal}
        onClose={closeModal}
        title={editCampaign ? `Edit: ${editCampaign.name}` : 'New Campaign'}
        description={
          editCampaign
            ? 'Update name, template, segment or schedule.'
            : 'A campaign sends bulk messages to all active members of a segment.'
        }
      >
        <div className="space-y-4">
          <Input
            label="Name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Summer promo 2026"
          />
          {!editCampaign && (
            <Select
              label="Channel"
              value={form.channel}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  channel: e.target.value as 'email' | 'sms',
                  templateId: '',
                }))
              }
            >
              <option value="email">Email</option>
              <option value="sms">SMS</option>
            </Select>
          )}
          <Select
            label="Template"
            value={form.templateId}
            onChange={(e) => setForm((f) => ({ ...f, templateId: e.target.value }))}
          >
            <option value="">Select a template…</option>
            {availableTemplates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Select>
          <Select
            label="Segment"
            hint="Audience for this campaign"
            value={form.segmentId}
            onChange={(e) => setForm((f) => ({ ...f, segmentId: e.target.value }))}
          >
            <option value="">All customers (no segment)</option>
            {segments.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.type})
              </option>
            ))}
          </Select>
          <Input
            label="Schedule at (optional)"
            type="datetime-local"
            hint="Leave blank to launch manually"
            value={form.scheduledAt}
            onChange={(e) => setForm((f) => ({ ...f, scheduledAt: e.target.value }))}
          />
          {(createMutation.isError || updateMutation.isError) && (
            <p className="text-xs text-red-500">
              {extractError(createMutation.error ?? updateMutation.error)}
            </p>
          )}
          <ModalFooter>
            <Button variant="ghost" size="sm" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              size="sm"
              loading={isSaving}
              disabled={!form.name || !form.templateId}
              onClick={handleSubmit}
            >
              {editCampaign ? 'Save changes' : 'Create campaign'}
            </Button>
          </ModalFooter>
        </div>
      </Modal>

      {/* Cancel confirm */}
      <ConfirmDialog
        open={!!cancelTarget}
        title="Cancel campaign?"
        message={`"${cancelTarget?.name}" will be stopped immediately. Sent messages will not be recalled.`}
        confirmLabel="Cancel campaign"
        loading={cancelMutation.isPending}
        onConfirm={() => cancelTarget && cancelMutation.mutate(cancelTarget.id)}
        onCancel={() => setCancelTarget(null)}
      />
    </div>
  );
}
